import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  PayeeRequirementPayerOverrideStatus,
  RequirementStatus,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";

import { useExtendExpirationDateModal } from "../components/ExtendExpirationDateModal/useExtendExpirationDateModal";
import { useMarkRequirementAsCompleteModal } from "../components/MarkRequirementAsCompleteModal";
import { useMarkRequirementAsIncompleteModal } from "../components/MarkRequirementAsIncompleteModal";
import { useReRequestRequirementModal } from "../components/ReRequestRequirementModal";
import { useRenewRequirementAsPayeeModal } from "../components/RenewRequirementModal/useRenewRequirementAsPayeeModal";
import { useRenewRequirementAsPayerModal } from "../components/RenewRequirementModal/useRenewRequirementAsPayerModal";
import { useRevertRequirementModal } from "../components/RevertRequirementModal";
import { selectorIsRequirementArchived } from "../selectors/selectorIsRequirementArchived";
import { selectorIsRequirementPostApproval } from "../selectors/selectorIsRequirementPostApproval";
import {
  ContextItem,
  RequirementAction,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementStatusViewProps,
  RequirementTableCallback
} from "../types";
import {
  ALL_REQUIREMENT_EVENTS,
  getIsCustomRequirement,
  getIsGeneralRequirement
} from "../utils";
import { isEmployeeTaxFormsRequirementType } from "../modules/EmployeeTaxForms/constants";
import { getEmployeeTaxFormsProgress } from "../modules/EmployeeTaxForms/configs/helpers/getEmployeeTaxFormsProgress";

const hideGroupLabelIfEmpty = (group: RequirementAction[]) => {
  return group.filter(action =>
    action.groupLabel
      ? group.some(action => !action.groupLabel && action.visible !== false)
      : true
  );
};

export const useRequirementDefaultActions = (
  contextType: RequirementContextType,
  callback: RequirementTableCallback
) => {
  const { openSnackbar } = useWSSnackbar();
  const markRequirementAsCompleteModal = useMarkRequirementAsCompleteModal();
  const markRequirementAsIncompleteModal =
    useMarkRequirementAsIncompleteModal();
  const reRequestRequirementModal = useReRequestRequirementModal();
  const revertRequirementModal = useRevertRequirementModal();
  const extendExpirationDateModal = useExtendExpirationDateModal();
  const renewRequirementAsPayerModal = useRenewRequirementAsPayerModal();
  const renewRequirementAsPayeeModal = useRenewRequirementAsPayeeModal();

  const handleRenewRequirementAsPayer =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      renewRequirementAsPayerModal.open({
        requirement,
        currentStatus,
        onSuccess(requirement) {
          callback(ALL_REQUIREMENT_EVENTS.Renew, requirement);
          openSnackbar({
            message: "Requirement renewed successfully",
            duration: 3000,
            type: "success"
          });
        }
      });
    };

  const handleRenewRequirementAsPayee =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      renewRequirementAsPayeeModal.open({
        requirement,
        currentStatus,
        onSuccess(requirement) {
          callback(ALL_REQUIREMENT_EVENTS.Renew, requirement);
          openSnackbar({
            message: "Requirement renewed successfully",
            duration: 3000,
            type: "success"
          });
        }
      });
    };

  const handleExtendExpirationDate =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      extendExpirationDateModal.open({
        requirement,
        onSuccess(requirement) {
          callback(ALL_REQUIREMENT_EVENTS.ExtendExpirationDate, requirement);
          openSnackbar({
            message: "Requirement expiration updated",
            duration: 3000,
            type: "success"
          });
        }
      });
    };

  const handleRevertRequirementStatus =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      revertRequirementModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.ResetStatus, requirement);
          openSnackbar({
            message: "Requirement status reverted successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleMarkRequirementAsCompleted =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      markRequirementAsCompleteModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement marked as complete successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleMarkRequirementAsIncomplete =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      markRequirementAsIncompleteModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement marked as incomplete successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleReRequestRequirement =
    (
      requirement: PayeeRequirement,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      reRequestRequirementModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement re-requested successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  return (
    requirement: PayeeRequirement,
    context: ContextItem,
    additionalDataItem: RequirementAdditionalData,
    currentStatus: RequirementStatusViewProps
  ): {
    extendToolbarActions(actions: RequirementAction[]): RequirementAction[];
  } => {
    let actions: RequirementAction[] = [];
    const isArchivedRequirement = selectorIsRequirementArchived(requirement);
    // Custom rows are client-built cell props and carry their own actions.
    const isRealRequirement = !getIsCustomRequirement(requirement);

    if (isRealRequirement && isArchivedRequirement) {
      actions = [];
    } else if (
      isRealRequirement &&
      contextType === RequirementContextType.Payee
    ) {
      const reRequestActions: RequirementAction[] = [
        {
          groupLabel: "Reset Requirement"
        },
        {
          label: "Re-request",
          icon: "reset",
          helpText:
            "Resets the status to incomplete and the contractor starts the requirement from scratch.",
          destructive: true,
          onClick: handleReRequestRequirement(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible: !getIsGeneralRequirement(requirement)
        }
      ];

      const extendExpirationActions: RequirementAction[] = [
        {
          groupLabel: "Manage expiration"
        },
        {
          label: "Request renewal",
          buttonIcon: null,
          helpText: requirement.expiresAt
            ? "Manually starts early renewal process prior to automatic reminders which start 30 days before expiration."
            : "Manually allows contractors to upload a new version of their document.",
          icon: "refresh-h",
          onClick: handleRenewRequirementAsPayer(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !getIsGeneralRequirement(requirement) &&
            requirement.status === RequirementStatus.COMPLETED
        },
        {
          label: "Update expiration date",
          buttonIcon: null,
          helpText: "Change the expiration date to a different date.",
          icon: "calendar",
          onClick: handleExtendExpirationDate(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !getIsGeneralRequirement(requirement) &&
            requirement.status === RequirementStatus.COMPLETED
        }
      ];

      const isReadyForApprove =
        requirement.status === RequirementStatus.PENDING_PAYER_REVIEW;

      const isPostApproval = selectorIsRequirementPostApproval(requirement);

      const defaultActions: RequirementAction[] = [
        {
          groupLabel: "Manage approvals"
        },
        {
          label: "Approve (mark complete)",
          buttonLabel: "Approve",
          buttonKind: isPostApproval ? undefined : "Primary",
          buttonOrder: 2,
          icon: "accept",
          buttonIcon: null,
          helpText:
            "Approves the submission and marks the requirement as complete and overwrites current status.",
          onClick: handleMarkRequirementAsCompleted(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            isReadyForApprove ||
            (isPostApproval &&
              requirement.status !== RequirementStatus.COMPLETED),
          mainAction: true
        },
        {
          label: "Reject (mark incomplete)",
          buttonLabel: "Reject",
          buttonIcon: null,
          buttonKind: isPostApproval ? undefined : "Secondary",
          // destructive: true,
          helpText:
            "Rejects the submission and marks the requirements as incomplete and overwrites current status.",
          icon: "dispute",
          onClick: handleMarkRequirementAsIncomplete(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            isReadyForApprove ||
            (isPostApproval &&
              requirement.status !== RequirementStatus.PENDING_COMPLETION),
          mainAction: true
        },
        {
          label: "Mark complete",
          icon: "accept",
          buttonIcon: null,
          helpText:
            "Overwritten status will be updated for both you and the contractor",
          onClick: handleMarkRequirementAsCompleted(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            requirement.type !== RequirementType.PAYOUT_METHOD &&
            requirement.payerOverrideStatus !==
              PayeeRequirementPayerOverrideStatus.COMPLETED
        },
        {
          label: "Mark incomplete",
          buttonIcon: null,
          helpText:
            "Overwritten status will be updated for both you and the contractor",
          icon: "dispute",
          onClick: handleMarkRequirementAsIncomplete(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            requirement.payerOverrideStatus !==
              PayeeRequirementPayerOverrideStatus.PENDING_COMPLETION
        },
        {
          label: "Revert to synced status",
          buttonIcon: null,
          helpText: "Undo overwritten status and revert to synced status",
          icon: "undo",
          onClick: handleRevertRequirementStatus(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            !!requirement?.payerOverrideStatus
        }
      ];

      actions = [
        ...hideGroupLabelIfEmpty(defaultActions),
        ...hideGroupLabelIfEmpty(extendExpirationActions),
        ...hideGroupLabelIfEmpty(reRequestActions)
      ];

      // Employee tax forms expose ONLY the two status overrides in the payer
      // dropdown. There is no review step (so no approve/reject) and forms are
      // not manually renewable (no renewal / expiration). "Mark incomplete" is
      // a STICKY PAYER OVERRIDE (applyPayerOverrides sets payerOverrideStatus:
      // Incomplete) — the contractor's completed filings are NOT invalidated
      // (a forms-service reset is deferred per review), so dataSourceStatus
      // stays Complete and the payer clears the override by marking the
      // requirement complete again.
      if (isEmployeeTaxFormsRequirementType(requirement.type)) {
        // Toggle the two overrides on the requirement's OVERRIDE state
        // (payerOverrideStatus), mirroring the default actions above —
        // basing them on the form-submission counters alone would dead-end:
        // after "Mark incomplete" the counters are still N/N, so "Mark
        // complete" would never render and the override would be irreversible.
        // Counters still gate "Mark incomplete" so it isn't offered before any
        // forms are submitted.
        const progress = getEmployeeTaxFormsProgress(
          requirement.dataSourceMetadata
        );
        const allFormsSubmitted =
          progress.total > 0 && progress.completed >= progress.total;
        const isMarkedIncompleteByPayer =
          requirement.payerOverrideStatus ===
          PayeeRequirementPayerOverrideStatus.PENDING_COMPLETION;

        actions = hideGroupLabelIfEmpty([
          {
            groupLabel: "Manage approvals"
          },
          {
            label: "Mark complete",
            icon: "accept",
            buttonIcon: null,
            helpText:
              "Overwritten status will be updated for both you and the contractor",
            onClick: handleMarkRequirementAsCompleted(
              requirement,
              context,
              additionalDataItem,
              currentStatus
            ),
            visible:
              isMarkedIncompleteByPayer ||
              (!allFormsSubmitted &&
                requirement.payerOverrideStatus !==
                  PayeeRequirementPayerOverrideStatus.COMPLETED)
          },
          {
            label: "Mark incomplete",
            buttonIcon: null,
            helpText:
              "Overwritten status will be updated for both you and the contractor",
            icon: "dispute",
            onClick: handleMarkRequirementAsIncomplete(
              requirement,
              context,
              additionalDataItem,
              currentStatus
            ),
            visible: !isMarkedIncompleteByPayer && allFormsSubmitted
          }
        ]);
      }
    } else if (
      isRealRequirement &&
      contextType === RequirementContextType.Payer
    ) {
      actions = [
        {
          label: "Start renewal",
          buttonIcon: null,
          icon: "refresh-h",
          onClick: handleRenewRequirementAsPayee(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !!requirement.manualRenewalAllowed &&
            requirement.status === RequirementStatus.COMPLETED
        }
      ];
    }

    return {
      extendToolbarActions: originalActions => {
        return [...originalActions, ...actions];
      }
    };
  };
};
